import { ShiftInviteRequestMessage } from "@src/appV2/Chat/ShiftInviteRequestMessage";
import { SendbirdChannelMessageProps } from "./chat.types";
import { ReviewSentHomeMessage } from "./ReviewSentHomeMessage";
import { SentHomeResponseMessage } from "./SentHomeResponseMessage";
import { MessageCustomTypes } from "@src/appV2/Chat/type";
import { PlacementApplicationMessage } from "./components/PlacementApplicationMessage";
import { isDefined } from "@clipboard-health/util-ts";
import { PlacementApplicationAutoInterviewInvite } from "./components/PlacementApplicationAutoInterviewInvite";
import { PlacementApplicationInvite } from "./components/PlacementApplicationInvite";
import { EveryMessage } from "@sendbird/uikit-react/types/types";
import { PlacementApplicationSuggestInterviewMessage } from "./components/PlacementApplicationSuggestInterviewMessage";
import { PlacementApplicationInterviewScheduledMessage } from "./components/PlacementApplicationInterviewScheduledMessage";
import {
  parseFacilityChatMessage,
  parseShiftInviteChatMessage,
  parseShiftTimeProposalMessage,
  parseVisitInviteChatMessage,
} from "../../appV2/Chat/utils";
import { PlacementApplicationInterviewCancelled } from "./components/PlacementApplicationInterviewCancelled";
import { WorkerCredentialRequestedMessage } from "@src/appV2/Chat/WorkerCredentialRequestedMessage";
import { IncomingUserChatMessage } from "../../appV2/Chat/IncomingUserChatMessage";
import { OutgoingUserChatMessage } from "../../appV2/Chat/OutgoingUserChatMessage";
import {
  hideMessage,
  isShiftInviteRequestMessage,
  isVisitInviteRequestMessage,
  shouldRenderDefaultAdminMessage,
} from "./utils/helpers";
import { isAdminMessage, isSendableMessage } from "./utils/helpers";
import { ShiftTimeProposalRequestMessage } from "../../appV2/Chat/ShiftTimeProposalRequestMessage";
import { VisitInviteRequestMessage } from "@src/appV2/Chat/VisitInviteRequestMessage";

// We are not able render this component as JSX with return type undefined
// as it's wrapped in an useMemo in it's source code
// [here](https://github.com/sendbird/sendbird-uikit-react/blob/main/src/modules/Channel/components/Message/index.tsx#L194)
export function renderSendbirdChannelMessage(props: SendbirdChannelMessageProps) {
  const {
    message,
    chainTop,
    chainBottom,
    userId,
    user,
    currentChannel,
    channelUrl,
    manageCredentialsModalState,
  } = props;

  const isFirstMessageInMessageChain = !chainTop;
  const isLastMessageInMessageChain = !chainBottom;

  const isDefinedCurrentChannelAndUrl = isDefined(currentChannel) && isDefined(channelUrl);

  if (hideMessage(message)) {
    return <></>;
  }

  if (shouldRenderDefaultAdminMessage(message)) {
    // Message payload has both worker and workplace facing messages, so parse the
    // the facility message and overwrite before rendering the default Sendbird admin message
    const parsedMessage = parseFacilityChatMessage(message);
    message.message = parsedMessage;
    return undefined as unknown as React.ReactElement;
  }

  if (message.customType === MessageCustomTypes.FCM_REQUEST) {
    return (
      <ReviewSentHomeMessage message={message} facilityId={userId} facilityUserId={user?._id} />
    );
  }

  if (isShiftInviteRequestMessage(message)) {
    const { shiftInviteId, status, facilityMessage } = parseShiftInviteChatMessage(message);

    if (!isDefined(shiftInviteId) || !isDefined(status)) {
      // Group shift invites do not have a shift invite ID and can be rendered as
      // default admin messages
      message.message = facilityMessage;
      return undefined as unknown as React.ReactElement;
    }

    return (
      <ShiftInviteRequestMessage
        message={facilityMessage}
        shiftInviteId={shiftInviteId}
        status={status}
      />
    );
  }

  if (isVisitInviteRequestMessage(message)) {
    const { visitId } = parseVisitInviteChatMessage(message);
    return <VisitInviteRequestMessage visitId={visitId} />;
  }

  if (message.customType === MessageCustomTypes.SHIFT_TIME_PROPOSAL_CREATED) {
    const parsedMessage = parseShiftTimeProposalMessage(message);

    if (!isDefined(parsedMessage)) {
      // Render default admin message
      return undefined as unknown as React.ReactElement;
    }

    const { shiftTimeProposalId, originalStartTime, originalEndTime } = parsedMessage;

    return (
      <ShiftTimeProposalRequestMessage
        shiftTimeProposalId={shiftTimeProposalId}
        originalStartTime={originalStartTime}
        originalEndTime={originalEndTime}
      />
    );
  }

  if (
    message.customType === MessageCustomTypes.FCM_APPROVAL ||
    message.customType === MessageCustomTypes.FCM_REJECT
  ) {
    return <SentHomeResponseMessage message={message} />;
  }

  if (
    isDefinedCurrentChannelAndUrl &&
    message.customType === MessageCustomTypes.PLACEMENT_APPLICATION
  ) {
    return (
      <PlacementApplicationMessage
        message={message}
        currentChannel={currentChannel}
        channelUrl={channelUrl}
      />
    );
  }

  if (
    isDefinedCurrentChannelAndUrl &&
    message.customType === MessageCustomTypes.PLACEMENT_APPLICATION_SUGGEST_INTERVIEW
  ) {
    return (
      <PlacementApplicationSuggestInterviewMessage
        message={message}
        currentChannel={currentChannel}
        channelUrl={channelUrl}
      />
    );
  }

  if (
    isDefinedCurrentChannelAndUrl &&
    message.customType === MessageCustomTypes.PLACEMENT_APPLICATION_CHAT_INTERVIEW_SCHEDULED
  ) {
    return (
      <PlacementApplicationInterviewScheduledMessage
        message={message}
        currentChannel={currentChannel}
      />
    );
  }

  if (
    isDefinedCurrentChannelAndUrl &&
    message.customType === MessageCustomTypes.PLACEMENT_APPLICATION_AUTO_INTERVIEW_INVITE
  ) {
    return (
      <PlacementApplicationAutoInterviewInvite
        message={message}
        currentChannel={currentChannel}
        channelUrl={channelUrl}
      />
    );
  }

  if (
    isDefinedCurrentChannelAndUrl &&
    message.customType === MessageCustomTypes.INTERVIEW_WORKER_CANCELLATION
  ) {
    return (
      <PlacementApplicationInterviewCancelled
        currentChannel={currentChannel}
        channelUrl={channelUrl}
      />
    );
  }

  if (
    isDefined(currentChannel) &&
    message.customType === MessageCustomTypes.PLACEMENT_APPLICATION_INVITE
  ) {
    return <PlacementApplicationInvite message={message as EveryMessage} />;
  }

  if (
    isAdminMessage(message) &&
    isDefined(currentChannel) &&
    message.customType === MessageCustomTypes.WORKER_CREDENTIAL_REQUEST
  ) {
    return (
      <WorkerCredentialRequestedMessage
        message={message}
        workerName={currentChannel.metadata?.hcpName}
        manageCredentialsModalState={manageCredentialsModalState}
      />
    );
  }

  if (isSendableMessage(message) && isDefined(message.data)) {
    const { sender } = message;
    const sentByMe = sender?.userId === userId;

    if (!sentByMe) {
      return (
        <IncomingUserChatMessage
          message={message}
          isFirstMessageInMessageChain={isFirstMessageInMessageChain}
          isLastMessageInMessageChain={isLastMessageInMessageChain}
        />
      );
    }

    if (isFirstMessageInMessageChain) {
      // We are only overriding outgoing messages that are first in the message chain
      // so that we can render the sender name, which is not rendered by Sendbird by default
      return <OutgoingUserChatMessage message={message} />;
    }
  }

  // We have to return undefined to render the sendbird message component and using any other value will make the message not visible on the UI.
  return undefined as unknown as React.ReactElement;
}
